import { useState, useEffect, useCallback } from 'react';
import i18n from '@/i18n';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useRuntimeAccess } from './useRuntimeAccess';
import { useTenantSelection } from '@/contexts/TenantSelectionContext';
import { useWhatsAppWebSessions } from './useWhatsAppWebSessions';
import { useNow } from './useNow';
import { effectiveWebStatus } from '@/lib/whatsapp-web-api';

interface ChannelStatus {
  name: string;
  display_name: string;
  is_configured: boolean;
  is_enabled: boolean;
  status: 'connected' | 'disconnected' | 'setup_required' | 'error';
  icon: string;
  config_id?: string;
  last_activity?: string;
  message_count_24h?: number;
  /** Active account ids of this channel, so a QR account can be judged by its own state. */
  active_config_ids?: string[];
}

interface UseChannelManagementReturn {
  availableChannels: ChannelStatus[];
  isLoading: boolean;
  error: string | null;
  refreshChannels: () => Promise<void>;
}

const CHANNEL_DISPLAY_CONFIG = {
  whatsapp: { display_name: 'WhatsApp', icon: 'MessageSquare' },
  instagram: { display_name: 'Instagram', icon: 'Instagram' },
  tiktok: { display_name: 'TikTok', icon: 'Tiktok' },
  facebook: { display_name: 'Facebook', icon: 'Facebook' },
  sms: { display_name: 'SMS', icon: 'Phone' },
  email: { display_name: 'Email', icon: 'Mail' },
  webchat: { display_name: 'Web Chat', icon: 'Globe' }
} as const;

const CACHE_TTL = 30000; // 30 seconds cache

// Module-level cache that persists across component lifecycles
let moduleCache: {data: ChannelStatus[], timestamp: number} | null = null;

// One read of the active channel rows at a time per channel list: the sidebar, the channel
// strip and the setup widgets all mount this hook on the same screen, and each used to send
// its own identical request before the first answered.
type ChannelConfigRow = { id: string; channel_name: string; api_provider: string; is_active: boolean | null; updated_at: string | null };
const inflightConfigReads = new Map<string, Promise<ChannelConfigRow[]>>();

function readActiveChannelConfigs(enabledChannels: string[]): Promise<ChannelConfigRow[]> {
  const key = [...enabledChannels].sort().join('|');
  const pending = inflightConfigReads.get(key);
  if (pending) return pending;
  const read = (async () => {
    const { data, error } = await supabase
      .from('api_configurations')
      .select('id, channel_name, api_provider, is_active, updated_at')
      .eq('is_active', true)
      .in('channel_name', enabledChannels);
    if (error) throw error;
    return (data ?? []) as ChannelConfigRow[];
  })().finally(() => {
    inflightConfigReads.delete(key);
  });
  inflightConfigReads.set(key, read);
  return read;
}

export const useChannelManagement = (): UseChannelManagementReturn => {
  const { userProfile, tenantData } = useAuth();
  const { selectedTenant } = useTenantSelection();
  // The runtime read (the tenant row plus three catalogs) only changes the answer when a platform admin
  // looks at a space other than their own; every other screen already holds the channel list in tenantData.
  const ownChannelsKnown = Boolean(tenantData?.enabled_channels) && (!selectedTenant || selectedTenant.id === tenantData?.id);
  const { currentConfig, isLoading: runtimeLoading } = useRuntimeAccess({ skip: ownChannelsKnown });
  // H-28, root cause (not the symptom the round-1 watchdog treated): useRuntimeAccess's
  // currentConfig only ever populates from TenantSelectionContext.selectedTenant, which
  // is the PLATFORM-ADMIN tenant switcher (see AppSidebar.tsx's own comment on why it
  // reads tenantData instead). For every ordinary tenant_admin or agent, selectedTenant
  // stays null forever, so currentConfig stays null forever, so the effect below —
  // gated on `currentConfig` being truthy — never called fetchChannelStatus even once.
  // isLoading kept its initial `true` value with no watchdog ever armed (the watchdog
  // lives inside fetchChannelStatus, which never ran), and AppSidebar rendered the
  // pulsing skeleton forever. Verified by reading TenantSelectionContext.tsx: for a
  // non-platform-admin, fetchTenants() never calls setSelectedTenant, so `currentConfig`
  // for e.g. "Operator Synthetic / agent" (role 'agent', not 'platform_admin') is null
  // on every render. `tenantData` (useAuth) is the one already fetched for any logged-in
  // tenant user regardless of role, and it carries the same `enabled_channels` field.
  const enabledChannels = currentConfig?.enabled_channels ?? tenantData?.enabled_channels ?? null;

  // Initialize from moduleCache if available and valid
  const [availableChannels, setAvailableChannels] = useState<ChannelStatus[]>(() => {
    if (moduleCache && Date.now() - moduleCache.timestamp < CACHE_TTL) {
      return moduleCache.data;
    }
    return [];
  });

  // Start with loading false if we have valid cache
  const [isLoading, setIsLoading] = useState(() => {
    if (moduleCache && Date.now() - moduleCache.timestamp < CACHE_TTL) {
      return false;
    }
    return true;
  });

  const [error, setError] = useState<string | null>(null);

  const fetchChannelStatus = useCallback(async () => {
    // H-28: the `api_configurations` query below is an unguarded `await` like
    // the ones traced in useRuntimeAccess — a stalled request here leaves
    // `isLoading` true forever too, and AppSidebar renders that as a skeleton
    // that never resolves. Same watchdog shape: turn "never answers" into an
    // error the operator can retry from.
    let timedOut = false;
    const watchdog = setTimeout(() => {
      timedOut = true;
      console.error('[useChannelManagement] Fetch timed out after 15s');
      setError(i18n.t('common:channelManagement.loadFailed'));
      setIsLoading(false);
    }, 15000);

    try {
      setIsLoading(true);
      setError(null);

      // Wait for the platform-admin runtime hook ONLY when it could still change the
      // answer — i.e. when nothing has supplied enabledChannels yet. For a regular
      // tenant_admin/agent, tenantData answers immediately and runtimeLoading finishing
      // later (it never will for them, see the H-28 comment above) must not block this
      // hook forever.
      if (runtimeLoading && !enabledChannels) {
        console.log('[useChannelManagement] Runtime still loading, waiting...');
        clearTimeout(watchdog);
        return; // Keep loading state, don't clear channels yet
      }

      // Only proceed if we have a channel list from either source (after runtime has
      // loaded, or tenantData already had it).
      if (!enabledChannels) {
        console.log('[useChannelManagement] No enabled_channels available after loading');
        clearTimeout(watchdog);
        setAvailableChannels([]);
        setIsLoading(false);
        return;
      }

      console.log('[useChannelManagement] Fetching channels for enabled:', enabledChannels);

      // Check module cache first
      if (moduleCache && Date.now() - moduleCache.timestamp < CACHE_TTL) {
        clearTimeout(watchdog);
        setAvailableChannels(moduleCache.data);
        setIsLoading(false);
        return;
      }

      // Optimize query: only select needed fields and filter by enabled channels
      const apiConfigs = await readActiveChannelConfigs(enabledChannels);

      // Build channel status only for channels the tenant has runtime access to
      const channels = enabledChannels
        .map(channelName => {
          const displayConfig = CHANNEL_DISPLAY_CONFIG[channelName as keyof typeof CHANNEL_DISPLAY_CONFIG];

          if (!displayConfig) {
            return null;
          }

          const config = apiConfigs?.find(c =>
            c.channel_name === channelName ||
            c.api_provider === channelName
          );

          const channelStatus = {
            name: channelName,
            display_name: displayConfig.display_name,
            icon: displayConfig.icon,
            is_configured: !!config,
            is_enabled: !!config?.is_active,
            config_id: config?.id,
            active_config_ids: (apiConfigs ?? []).filter(c => c.api_provider === channelName || c.channel_name === channelName).map(c => c.id),
            status: config?.is_active
              ? 'connected' as const
              : config
                ? 'disconnected' as const
                : 'setup_required' as const,
            last_activity: config?.updated_at,
            message_count_24h: 0
          };

          return channelStatus;
        })
        .filter(Boolean) as ChannelStatus[];

      // Update module cache
      moduleCache = { data: channels, timestamp: Date.now() };
      console.log('[useChannelManagement] Successfully loaded channels:', channels.length, channels.map(c => c.name));
      setAvailableChannels(channels);
    } catch (err) {
      // A late rejection after the watchdog already resolved this to an error
      // would just restate the same failure a beat later; `timedOut` keeps the
      // two from fighting over which message wins.
      if (!timedOut) {
        console.error('useChannelManagement: Failed to fetch channel status:', err);
        setError(err instanceof Error ? err.message : i18n.t('common:channelManagement.loadFailed'));
      }
    } finally {
      clearTimeout(watchdog);
      if (!timedOut) setIsLoading(false);
    }
  }, [enabledChannels, runtimeLoading]);

  const refreshChannels = useCallback(async () => {
    moduleCache = null; // Clear module cache on manual refresh
    inflightConfigReads.clear();
    await fetchChannelStatus();
  }, [fetchChannelStatus]);

  useEffect(() => {
    // Fire as soon as EITHER source can answer (tenantData, for any logged-in tenant
    // user, or currentConfig, populated only for platform admins) — OR once the one
    // source that will ever resolve for this user (runtimeLoading, tracked by
    // useRuntimeAccess's own watchdog) is done, even with nothing to show. Requiring
    // BOTH `!runtimeLoading` AND `currentConfig` truthy was the H-28 bug: currentConfig
    // never arrives for a regular tenant_admin/agent, so that guard never passed and
    // this effect never called fetchChannelStatus, leaving isLoading stuck at its
    // initial `true` with no watchdog ever armed to correct it.
    if (userProfile && (enabledChannels || !runtimeLoading)) {
      fetchChannelStatus();
    }
  }, [userProfile, enabledChannels, runtimeLoading, fetchChannelStatus]);

  // A QR WhatsApp account is not "connected" because its row is active: the indicator reads the same
  // effective state as the account row (a linked phone, a live bridge lease, an assistant).
  const { sessions: webSessions } = useWhatsAppWebSessions(tenantData?.id);
  const now = useNow();
  const channels = availableChannels.map((channel) => {
    if (channel.name !== 'whatsapp' || webSessions.length === 0) return channel;
    const ids = new Set(channel.active_config_ids ?? []);
    const webActive = webSessions.filter((session) => ids.has(session.api_configuration_id));
    if (webActive.length === 0) return channel;
    const webIds = new Set(webSessions.map((session) => session.api_configuration_id));
    const metaActive = [...ids].some((id) => !webIds.has(id));
    const shown = webActive.map((session) => effectiveWebStatus(session, now));
    const status = metaActive || shown.includes('connected')
      ? ('connected' as const)
      : shown.some((s) => s === 'waiting_qr' || s === 'qr_pending' || s === 'connecting')
        ? ('setup_required' as const)
        : ('error' as const);
    return { ...channel, status };
  });

  return {
    availableChannels: channels,
    isLoading,
    error,
    refreshChannels
  };
};